import { Badge, IconButton, Theme, useMediaQuery, useTheme } from "@mui/material";
import { usePopover } from "src/hooks/use-popover";
import { observer } from "mobx-react-lite";
import { Notification } from "src/icons/notification";
import RecentNotificationsList from "./recent-notifications-list";
import { NotificationI } from "../../types/responses/notification.response";
import { FetchNextPageOptions, InfiniteQueryObserverResult } from "@tanstack/react-query";
import { PaginatedDataV4 } from "src/types/responses/paginated-data-v4";
import { ApiError } from "src/types/interfaces/api-error";
import { Dispatch, SetStateAction } from "react";
import { NotificationSearchState } from "../../types/payloads/notification-search-state.payload";

type Props = {
  notifications: NotificationI[];
  unreadNotificationsCount: number;
  isLoading: boolean;
  hasNextPage: boolean;
  fetchNextPage: (
    options?: FetchNextPageOptions,
  ) => Promise<InfiniteQueryObserverResult<PaginatedDataV4<NotificationI>, ApiError>>;
  setSearchState: Dispatch<SetStateAction<NotificationSearchState>>;
  onNotificationMarkAsRead: (token: string) => void;
  onNotificationsMarkAsRead: (filters?: {}) => void;
  onNotificationMarkAsUnread: (token: string) => void;
  isChangingReadStatusDisabled: boolean;
};

const NotificationsPopoverComponent = ({
  notifications,
  unreadNotificationsCount,
  isLoading,
  hasNextPage,
  fetchNextPage,
  setSearchState,
  isChangingReadStatusDisabled,
  onNotificationMarkAsRead,
  onNotificationMarkAsUnread,
  onNotificationsMarkAsRead,
}: Props) => {
  const {
    palette: { mode },
  } = useTheme();

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));

  const [anchorRef, open, handleOpen, handleClose] = usePopover();

  return (
    <>
      <IconButton
        color="primary"
        onClick={handleOpen}
        disabled={isLoading}
        ref={anchorRef}
        size={isMobile ? "small" : "large"}
        sx={{ mx: 1, display: "inline-flex" }}>
        <Badge
          badgeContent={unreadNotificationsCount}
          color="error"
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}>
          <Notification
            sx={{
              color: mode === "dark" ? "#fff" : "#000",
              width: "22px",
            }}
          />
        </Badge>
      </IconButton>
      <RecentNotificationsList
        open={open}
        handleClose={handleClose}
        anchorRef={anchorRef}
        notifications={notifications}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        setSearchState={setSearchState}
        isLoading={isLoading}
        isChangingReadStatusDisabled={isChangingReadStatusDisabled}
        onNotificationMarkAsRead={onNotificationMarkAsRead}
        onNotificationMarkAsUnread={onNotificationMarkAsUnread}
        onNotificationsMarkAsRead={onNotificationsMarkAsRead}
      />
    </>
  );
};

export const NotificationsPopover = observer(NotificationsPopoverComponent);
